/**
 * A payment link can stop being payable between the page load and the payment
 * attempt — the classic case is the same link open in two tabs, where the second
 * tab only finds out when it tries to start a payment. Backend reports those
 * states with an error key; without this mapping they fall into the generic
 * "payment system unavailable" alert, which is both wrong and offers a retry
 * that can never succeed.
 */

/** Mirrors ErrorKey in backend/src/shared/utils/api/error-constants.ts. */
const ALREADY_PAID_KEY = "error-payment-link-already-paid";
const DEADLINE_EXCEEDED_KEY = "error-payment-link-deadline-exceeded";

/** Pre-key backend builds only carried these messages. */
const ALREADY_PAID_MESSAGE = "Already paid";
const DEADLINE_EXCEEDED_MESSAGE = "Deadline exceeded";

type PaymentLinkFailure = { key?: string; message?: string };

const matches = (error: PaymentLinkFailure, key: string, legacyMessage: string): boolean =>
  error.key === key || (!error.key && !!error.message?.includes(legacyMessage));

/**
 * The page a rejected payment link should send the guest to, or null when the
 * failure says nothing about the link's state and the caller should handle it.
 */
export const resolvePaymentLinkRedirect = (
  error: PaymentLinkFailure,
  slug: string | null,
): string | null => {
  // The confirmation page resolves the paid payment by slug, so a missing slug
  // leaves nothing to show.
  if (matches(error, ALREADY_PAID_KEY, ALREADY_PAID_MESSAGE)) {
    return slug ? `/p/${slug}/confirmation` : null;
  }
  if (matches(error, DEADLINE_EXCEEDED_KEY, DEADLINE_EXCEEDED_MESSAGE)) {
    return "/p/expired";
  }
  return null;
};
